import { useQuery } from "@apollo/client/react";
import { GetCurrentTeamDocument, SubscriptionStatus, type TeamFragment } from "../gql/generated/graphqlSchema.tsx";

export type AccessState =
  | { readonly kind: "none" }
  | { readonly kind: "trialEndingSubtle"; readonly endsAt: string; readonly daysLeft: number }
  | { readonly kind: "trialEndingProminent"; readonly endsAt: string; readonly daysLeft: number }
  | { readonly kind: "locked"; readonly endedAt: string }
  | { readonly kind: "pastDue" }
  // `endsAt` is null when Stripe schedules an open-ended cancellation (rare) or when
  // the webhook updates ordering hasn't landed yet. Banner copy must tolerate the null.
  | { readonly kind: "cancelScheduled"; readonly endsAt: string | null };

const SUBTLE_THRESHOLD_DAYS = 7;
const PROMINENT_THRESHOLD_DAYS = 2;
// Cancellation can be scheduled weeks in advance. Don't nag the user about it
// until the end is actually near; the Billing tab always shows the exact date
// for users who want it.
const CANCEL_BANNER_THRESHOLD_DAYS = 3;

export const deriveAccessState = (team: TeamFragment, now: Date = new Date()): AccessState => {
  if (team.subscriptionStatus === SubscriptionStatus.Active) {
    if (team.cancelAt != null && daysUntil(team.cancelAt, now) <= CANCEL_BANNER_THRESHOLD_DAYS) {
      return { kind: "cancelScheduled", endsAt: team.cancelAt };
    }
    return { kind: "none" };
  }
  if (team.subscriptionStatus === SubscriptionStatus.PastDue) {
    return { kind: "pastDue" };
  }
  if (!team.hasFullAccess) {
    return { kind: "locked", endedAt: team.freeAccessUntil };
  }
  const endsAt = new Date(team.freeAccessUntil);
  const msLeft = endsAt.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
  if (daysLeft > SUBTLE_THRESHOLD_DAYS) return { kind: "none" };
  if (daysLeft > PROMINENT_THRESHOLD_DAYS) {
    return { kind: "trialEndingSubtle", endsAt: team.freeAccessUntil, daysLeft };
  }
  return { kind: "trialEndingProminent", endsAt: team.freeAccessUntil, daysLeft };
};

const daysUntil = (isoString: string, now: Date): number => {
  const ms = new Date(isoString).getTime() - now.getTime();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

export const useTeamAccessState = (): { readonly state: AccessState; readonly team: TeamFragment | null } => {
  const { data } = useQuery(GetCurrentTeamDocument);
  const team = data?.currentTeam ?? null;
  const state: AccessState = team ? deriveAccessState(team) : { kind: "none" };
  return { state, team };
};
